import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Button, Badge, Spinner, Alert, Modal } from 'react-bootstrap';
import { supabase } from './supabaseClient';

function AdminAccessRequests() {
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingId, setProcessingId] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('access_requests')
        .select('*')
        .order('requested_at', { ascending: false });

      if (error) throw error;

      setRequests(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching requests:', err);
      setError('Failed to load access requests');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAction = async (requestId, action) => {
    setProcessingId(requestId);
    setError(null);

    try {
      // Get current session
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        throw new Error('You must be logged in to perform this action');
      }

      // Call Edge Function to approve/reject
      const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
      const response = await fetch(`${SUPABASE_URL}/functions/v1/approve-access-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          requestId,
          action,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to process request');
      }

      // Refresh the list
      await fetchRequests();
      setShowConfirmModal(false);
      setConfirmAction(null);
    } catch (err) {
      console.error('Error processing request:', err);
      setError(err.message || 'Failed to process request');
    } finally {
      setProcessingId(null);
    }
  };

  const openConfirmModal = (requestId, action) => {
    setConfirmAction({ requestId, action });
    setShowConfirmModal(true);
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'warning',
      approved: 'success',
      rejected: 'danger',
    };
    return <Badge bg={variants[status] || 'secondary'}>{status.toUpperCase()}</Badge>;
  };

  if (isLoading) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Access Requests</h1>
        <Button variant="outline-secondary" size="sm" onClick={fetchRequests}>
          Refresh
        </Button>
      </div>

      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      <Card>
        <Card.Body>
          {requests.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <p>No access requests found.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover>
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Company</th>
                    <th>Reason</th>
                    <th>Requested</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map((request) => (
                    <tr key={request.id}>
                      <td>{getStatusBadge(request.status)}</td>
                      <td><strong>{request.full_name}</strong></td>
                      <td>{request.email}</td>
                      <td>{request.company || <span className="text-muted">N/A</span>}</td>
                      <td>
                        {request.reason ? (
                          <small>{request.reason.substring(0, 100)}{request.reason.length > 100 ? '...' : ''}</small>
                        ) : (
                          <span className="text-muted">N/A</span>
                        )}
                      </td>
                      <td>
                        <small className="text-muted">
                          {new Date(request.requested_at).toLocaleDateString()}<br />
                          {new Date(request.requested_at).toLocaleTimeString()}
                        </small>
                      </td>
                      <td>
                        {request.status === 'pending' && (
                          <div className="btn-group btn-group-sm">
                            <Button
                              variant="success"
                              size="sm"
                              onClick={() => openConfirmModal(request.id, 'approved')}
                              disabled={processingId === request.id}
                            >
                              {processingId === request.id ? (
                                <Spinner as="span" animation="border" size="sm" />
                              ) : (
                                'Approve'
                              )}
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => openConfirmModal(request.id, 'rejected')}
                              disabled={processingId === request.id}
                            >
                              Reject
                            </Button>
                          </div>
                        )}
                        {request.status !== 'pending' && (
                          <small className="text-muted">
                            {request.status === 'approved' && 'User created'}
                            {request.status === 'rejected' && 'Request denied'}
                          </small>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Action</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {confirmAction && (
            <p>
              Are you sure you want to <strong>{confirmAction.action}</strong> this access request?
              {confirmAction.action === 'approved' && (
                <span className="d-block mt-2 text-muted small">
                  This will create a user account and send them a magic link to sign in.
                </span>
              )}
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            Cancel
          </Button>
          <Button
            variant={confirmAction?.action === 'approved' ? 'success' : 'danger'}
            onClick={() => confirmAction && handleAction(confirmAction.requestId, confirmAction.action)}
          >
            Confirm {confirmAction?.action === 'approved' ? 'Approval' : 'Rejection'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default AdminAccessRequests;
